import React, { createContext, useContext, useEffect, useReducer, ReactNode } from 'react';
import { authAPI } from '../services/authAPI';
import {
  loadAutoLogoutSettings,
  saveAutoLogoutSettings,
  getMinutesUntilLogout
} from '../config/autoLogoutConfig';

// ユーザー情報の型定義
export interface User {
  id: number;
  username: string;
  displayName: string;
  roleLevel: number;
  roleLabel: string;
}

// 認証状態の型定義
interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  temporaryAdminMode: boolean;
  temporaryAdminExpiry: Date | null;
}

// アクションの型定義
type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'TOKEN_VERIFIED'; payload: { user: User } }
  | { type: 'TOKEN_INVALID' }
  | { type: 'ENABLE_TEMP_ADMIN'; payload: { expiry: Date } }
  | { type: 'DISABLE_TEMP_ADMIN' };

// コンテキストの型定義
interface AuthContextType {
  state: AuthState;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  verifyToken: () => Promise<boolean>;
  hasPermission: (requiredRoleLevel: number) => boolean;
  isAdmin: () => boolean;
  isDeveloper: () => boolean;
  isStaff: () => boolean;
  enableTemporaryAdmin: (password: string) => Promise<boolean>;
  disableTemporaryAdmin: () => void;
}

// 初期状態
const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('waton_token'),
  isLoading: false,
  isAuthenticated: false,
  temporaryAdminMode: false,
  temporaryAdminExpiry: null,
};

// Reducer関数
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isLoading: false,
        isAuthenticated: true,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isLoading: false,
        isAuthenticated: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isLoading: false,
        isAuthenticated: false,
      };
    case 'TOKEN_VERIFIED':
      return {
        ...state,
        user: action.payload.user,
        isLoading: false,
        isAuthenticated: true,
      };
    case 'TOKEN_INVALID':
      return {
        ...state,
        user: null,
        token: null,
        isLoading: false,
        isAuthenticated: false,
      };
    case 'ENABLE_TEMP_ADMIN':
      return {
        ...state,
        temporaryAdminMode: true,
        temporaryAdminExpiry: action.payload.expiry,
      };
    case 'DISABLE_TEMP_ADMIN':
      return {
        ...state,
        temporaryAdminMode: false,
        temporaryAdminExpiry: null,
      };
    default:
      return state;
  }
};

// コンテキスト作成
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// プロバイダー
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // ログイン関数（PINログインにも対応）
  const login = async (username: string, password: string, isPinLogin: boolean = false): Promise<boolean> => {
    dispatch({ type: 'LOGIN_START' });

    try {
      // PINログインの場合は既にトークンが返されているので、直接成功処理
      if (isPinLogin) {
        // PINログインは別途処理されるため、ここでは何もしない
        return true;
      }

      const response = await authAPI.login(username, password);

      if (response.success) {
        const { user, token } = response;

        // トークンをローカルストレージに保存
        localStorage.setItem('waton_token', token);

        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user, token }
        });

        return true;
      } else {
        dispatch({ type: 'LOGIN_FAILURE' });
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      dispatch({ type: 'LOGIN_FAILURE' });
      return false;
    }
  };

  // ログアウト関数
  const logout = async () => {
    try {
      // サーバーにログアウトを通知
      if (state.token) {
        await authAPI.logout();
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // ローカルストレージからトークンを削除
      localStorage.removeItem('waton_token');
      dispatch({ type: 'LOGOUT' });
      // ログインページにリダイレクト
      window.location.href = '/login';
    }
  };

  // トークン検証関数
  const verifyToken = async (): Promise<boolean> => {
    if (!state.token) {
      return false;
    }

    try {
      const response = await authAPI.verifyToken();
      
      if (response.success) {
        dispatch({ 
          type: 'TOKEN_VERIFIED', 
          payload: { user: response.user } 
        });
        return true;
      } else {
        localStorage.removeItem('waton_token');
        dispatch({ type: 'TOKEN_INVALID' });
        return false;
      }
    } catch (error) {
      console.error('Token verification error:', error);
      localStorage.removeItem('waton_token');
      dispatch({ type: 'TOKEN_INVALID' });
      return false;
    }
  };

  // 権限チェック関数
  const hasPermission = (requiredRoleLevel: number): boolean => {
    if (!state.user) return false;
    // 一時管理者モードが有効で期限内の場合は管理者権限を付与
    if (state.temporaryAdminMode && state.temporaryAdminExpiry) {
      if (new Date() < new Date(state.temporaryAdminExpiry)) {
        return true;
      } else {
        // 期限切れの場合は自動的に無効化
        dispatch({ type: 'DISABLE_TEMP_ADMIN' });
      }
    }
    // roleLevel: 2=管理者, 1=マネージャー, 0=スタッフ
    // 数値が大きいほど権限が高い
    return state.user.roleLevel >= requiredRoleLevel;
  };

  // 管理者権限チェック（開発者・オーナー用）
  const isAdmin = (): boolean => {
    if (!state.user) {
      console.log('[isAdmin] No user in state');
      return false;
    }

    console.log('[isAdmin] Checking admin for user:', {
      username: state.user.username,
      role: state.user.role,
      roleLevel: state.user.roleLevel,
      roleType: typeof state.user.roleLevel
    });

    // 一時管理者モードが有効で期限内の場合は管理者権限を付与
    if (state.temporaryAdminMode && state.temporaryAdminExpiry) {
      if (new Date() < new Date(state.temporaryAdminExpiry)) {
        return true;
      } else {
        // 期限切れの場合は自動的に無効化
        dispatch({ type: 'DISABLE_TEMP_ADMIN' });
      }
    }

    // role が developer または admin の場合は管理者
    if (state.user.role === 'developer' || state.user.role === 'admin') {
      console.log('[isAdmin] User has developer/admin role - returning true');
      return true;
    }

    // roleLevel 2 が管理者
    const isRoleLevelAdmin = state.user.roleLevel >= 2;
    console.log('[isAdmin] roleLevel check:', isRoleLevelAdmin);
    return isRoleLevelAdmin;
  };

  // 開発者権限チェック（role_level 3）
  const isDeveloper = (): boolean => {
    if (!state.user) return false;
    return state.user.roleLevel >= 3;
  };

  // スタッフ権限チェック
  const isStaff = (): boolean => {
    if (!state.user) return false;
    // roleLevel 0 以上なら全員アクセス可能
    return state.user.roleLevel >= 0;
  };

  // 一時管理者権限を有効化（パスワード確認付き）
  const enableTemporaryAdmin = async (password: string): Promise<boolean> => {
    try {
      // カツ氏の管理者パスワードを確認（バックエンドで検証）
      const response = await authAPI.verifyAdminPassword(password);

      if (response.success) {
        // 30分間の一時管理者権限を付与
        const expiry = new Date();
        expiry.setMinutes(expiry.getMinutes() + 30);

        dispatch({
          type: 'ENABLE_TEMP_ADMIN',
          payload: { expiry }
        });

        // 期限切れ時に自動無効化するタイマーを設定
        setTimeout(() => {
          dispatch({ type: 'DISABLE_TEMP_ADMIN' });
        }, 30 * 60 * 1000); // 30分

        return true;
      }
      return false;
    } catch (error) {
      console.error('Temporary admin enable error:', error);
      return false;
    }
  };

  // 一時管理者権限を無効化
  const disableTemporaryAdmin = () => {
    dispatch({ type: 'DISABLE_TEMP_ADMIN' });
  };

  // 自動ログアウトのチェック関数
  const checkAutoLogout = () => {
    if (!state.isAuthenticated) return;

    const settings = loadAutoLogoutSettings();
    if (!settings.enabled) return;

    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();

    // 設定時刻にログアウト
    if (hours === settings.hour && minutes === settings.minute) {
      console.log(`自動ログアウト時刻になりました（${settings.hour}:${String(settings.minute).padStart(2, '0')}）`);

      // ログアウト実行日時を保存
      saveAutoLogoutSettings({
        ...settings,
        lastLogout: new Date().toISOString()
      });

      // アラート表示（オプション）
      alert(`自動ログアウト時刻（${settings.hour}:${String(settings.minute).padStart(2, '0')}）になりました。\n再度ログインしてください。`);

      logout();
    }

    // 事前警告（10分前、5分前、1分前）
    const minutesUntil = getMinutesUntilLogout(settings);
    if ([10, 5, 1].includes(minutesUntil)) {
      console.log(`あと${minutesUntil}分で自動ログアウトします`);
      // ここに通知処理を追加可能（トーストメッセージなど）
    }
  };

  // 初回レンダリング時にトークンを検証
  useEffect(() => {
    if (state.token && !state.user) {
      verifyToken();
    }
  }, [state.token]);

  // 自動ログアウトタイマーの設定
  useEffect(() => {
    // 毎分チェック（3:59になったらログアウト）
    const intervalId = setInterval(checkAutoLogout, 60000); // 60秒ごとにチェック

    // 初回チェック
    checkAutoLogout();

    // クリーンアップ
    return () => {
      clearInterval(intervalId);
    };
  }, [state.isAuthenticated]); // 認証状態が変わったら再設定

  const value: AuthContextType = {
    state,
    login,
    logout,
    verifyToken,
    hasPermission,
    isAdmin,
    isDeveloper,
    isStaff,
    enableTemporaryAdmin,
    disableTemporaryAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// カスタムフック
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// 高階コンポーネント: 認証が必要なコンポーネントをラップ
interface RequireAuthProps {
  children: ReactNode;
  requiredRoleLevel?: number; // 必要な権限レベル
  fallback?: ReactNode; // 権限がない場合に表示するコンポーネント
}

export const RequireAuth: React.FC<RequireAuthProps> = ({ 
  children, 
  requiredRoleLevel = 2, // デフォルトはスタッフレベル
  fallback 
}) => {
  const { state, hasPermission } = useAuth();

  // ロード中の場合
  if (state.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // 未認証の場合
  if (!state.isAuthenticated) {
    return fallback || (
      <div className="text-center py-8">
        <p className="text-gray-600">ログインが必要です</p>
      </div>
    );
  }

  // 権限が不足している場合
  if (!hasPermission(requiredRoleLevel)) {
    return fallback || (
      <div className="text-center py-8">
        <p className="text-red-600">この機能を使用する権限がありません</p>
      </div>
    );
  }

  return <>{children}</>;
};